export type PlanReviewActionParams = Record<string, unknown>
export type PlanReviewActionParamSource = PlanReviewActionParams | string | null | undefined

const PLAN_REVIEW_ACTION_PARAM_FIELDS = {
  category: 'Category',
  keyObjective: 'KeyObjective',
  actualsValueType: 'ActualsValueType',
  bySubCategory: 'BySubCategory',
} as const

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizeActionParamValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return value.trim() || undefined
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  return undefined
}

export const resolvePlanReviewActionParamValue = (
  actionParams: PlanReviewActionParams | null | undefined,
  fieldName: string
) => {
  return normalizeActionParamValue(actionParams?.[fieldName])
}

export const normalizePlanReviewActionParams = (
  actionParam: PlanReviewActionParamSource
): PlanReviewActionParams => {
  if (isRecord(actionParam)) {
    return actionParam
  }

  if (typeof actionParam === 'string') {
    try {
      const parsedActionParam = JSON.parse(actionParam)

      if (isRecord(parsedActionParam)) {
        return parsedActionParam
      }
    } catch {
      return {}
    }
  }

  return {}
}

export const resolvePlanReviewCategoryActionParam = (
  actionParams: PlanReviewActionParams | null | undefined
) => resolvePlanReviewActionParamValue(actionParams, PLAN_REVIEW_ACTION_PARAM_FIELDS.category)

export const resolvePlanReviewKeyObjectiveActionParam = (
  actionParams: PlanReviewActionParams | null | undefined
) => resolvePlanReviewActionParamValue(actionParams, PLAN_REVIEW_ACTION_PARAM_FIELDS.keyObjective)

export const resolvePlanReviewActualsValueTypeActionParam = (
  actionParams: PlanReviewActionParams | null | undefined
) =>
  resolvePlanReviewActionParamValue(actionParams, PLAN_REVIEW_ACTION_PARAM_FIELDS.actualsValueType)

/** `BySubCategory` 1|0: the action works on the SubCategory breakdown instead of the category aggregate */
export const resolvePlanReviewBySubCategoryActionParam = (
  actionParams: PlanReviewActionParams | null | undefined
) => {
  const value = resolvePlanReviewActionParamValue(
    actionParams,
    PLAN_REVIEW_ACTION_PARAM_FIELDS.bySubCategory
  )

  return value === '1' || value === 'true'
}
